import { Injectable } from '@angular/core';
import { ModelData, TrajectoryStation, WellData, WellboreData, TrajectoryData, TargetsModel, TargetSetDto, TargetDto, QuerySummaryDto, TargetWellboreResolution, TargetParentPayloads } from '../models/interfaces';

type AzimuthReference = 'Grid' | 'True' | 'Unknown';

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function normalizeBooleanFlag(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  if (typeof value === 'number') return value !== 0;
  return Boolean(value);
}

function normalizeAzimuthReference(value: unknown): AzimuthReference {
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    if (lower === 'true' || lower === 'true north') return 'True';
    if (lower === 'grid' || lower === 'grid north') return 'Grid';
  }
  return 'Unknown';
}

@Injectable({ providedIn: 'root' })
export class ModelService {
  private cachedModel: ModelData | null = null;
  private customModelData: ModelData | null = null;
  // Targets are imported separately from the trajectory model. Enriched
  // templates can also carry the minimum well and wellbore parent records.
  private customTargets: TargetsModel | null = null;

  private readonly defaultModel: ModelData = {
    well: {
      id: 'Terre_001',
      name: 'Well_Terre_001',
      fetchable: true,
      location: {
        coordinate_reference_system: 'UTM Zone 50S - WGS84, Meters',
        coordinates_type: 'Grid',
        azimuth_reference: 'Grid',
        northing: 7724902,
        easting: -171734
      },
      is_off_shore: true,
      ground_level: -100,
      rkb: 10,
      uwi: 'Terre_001_UWI',
      field_name: 'JerryTree'
    },
    wellbore: {
      id: 'wellbore_Terre_001',
      name: 'wellbore_Terre_001',
      fetchable: true,
      well_id: 'Terre_001',
      ubhi: 'Terre_001_UBHI'
    },
    trajectory: {
      id: 'trajectory_Terre_001',
      name: 'Trajectory from Koble',
      wellbore_id: 'wellbore_Terre_001',
      fetchable: true,
      is_actual: false,
      is_working_survey: true,
      rkb: 10,
      azimuth_reference: 'Grid',
      vsec: { origin_ns: 7724902, origin_ew: -171734, azimuth: 0 },
      stations: [
        { md: 0, inclination: 0, azimuth: 0, comment: 'Initial station' },
        { md: 200, inclination: 0.087, azimuth: 0.262, comment: '' },
        { md: 400, inclination: 0.087, azimuth: 0.349, comment: '' },
        { md: 600, inclination: 0.175, azimuth: 0.349, comment: '' },
        { md: 800, inclination: 0.262, azimuth: 0.349, comment: '' },
        { md: 1200, inclination: 0.611, azimuth: 0.349, comment: '' }
      ]
    }
  };

  getModel(): ModelData {
    if (!this.cachedModel) {
      const source = this.customModelData
        ? deepClone(this.customModelData)
        : deepClone(this.defaultModel);
      this.cachedModel = this.buildAndValidateModel(source);
    }
    return this.cachedModel;
  }

  hasModel(): boolean {
    return this.customModelData !== null;
  }

  replaceModel(modelData: ModelData): ModelData {
    const sanitized = this.sanitizeModelData(modelData);
    this.customModelData = sanitized;
    this.cachedModel = deepClone(sanitized);
    return this.cachedModel;
  }

  getPayloads(): { wells: WellData[]; wellbores: WellboreData[]; trajectories: TrajectoryData[] } {
    const model = this.getModel();
    const now = new Date().toISOString();

    // Build well payload — only API-accepted fields
    const well = deepClone(model.well);
    const wellPayload: WellData = {
      id: well.id,
      name: well.name,
      fetchable: well.fetchable,
      location: {
        coordinate_reference_system: well.location.coordinate_reference_system || undefined as any,
        coordinates_type: well.location.coordinates_type || undefined as any,
        azimuth_reference: normalizeAzimuthReference(well.location.azimuth_reference),
        northing: well.location.northing || undefined as any,
        easting: well.location.easting || undefined as any,
        latitude: well.location.latitude || undefined as any,
        longitude: well.location.longitude || undefined as any
      },
      is_off_shore: well.is_off_shore,
      ground_level: well.ground_level,
      rkb: well.rkb,
      uwi: well.uwi || undefined as any,
      field_name: well.field_name || undefined as any,
      timestamp: now
    };

    // Build wellbore payload
    const wellbore = deepClone(model.wellbore);
    const wellborePayload: WellboreData = {
      id: wellbore.id,
      name: wellbore.name,
      fetchable: wellbore.fetchable,
      well_id: wellbore.well_id,
      ubhi: wellbore.ubhi || undefined as any,
      timestamp: now
    };

    // Build trajectory payload — strip extra station fields (API only accepts md, inclination, azimuth, comment)
    const trajectory = deepClone(model.trajectory);
    const azRef = normalizeAzimuthReference(trajectory.azimuth_reference);
    const isActual = !!trajectory.is_actual;
    // API constraint: is_working_survey must be false when is_actual is false
    const isWorkingSurvey = isActual ? !!trajectory.is_working_survey : false;
    const trajectoryPayload: TrajectoryData = {
      id: trajectory.id,
      name: trajectory.name,
      fetchable: trajectory.fetchable,
      wellbore_id: trajectory.wellbore_id,
      is_actual: isActual,
      is_working_survey: isWorkingSurvey,
      rkb: trajectory.rkb,
      azimuth_reference: azRef,
      vsec: {
        origin_ns: trajectory.vsec.origin_ns,
        origin_ew: trajectory.vsec.origin_ew,
        azimuth: this.clampAzimuth(trajectory.vsec.azimuth)
      },
      stations: trajectory.stations.map(s => ({
        md: s.md,
        inclination: Math.max(0, Math.min(Math.PI, s.inclination)),
        azimuth: this.clampAzimuth(s.azimuth),
        comment: s.comment || ''
      })),
      timestamp: now,
      survey_date: now
    };

    return {
      wells: [wellPayload],
      wellbores: [wellborePayload],
      trajectories: [trajectoryPayload]
    };
  }

  // --- Targets (separate file, separate payload) ---

  /** Store a parsed targets model (from a Targets workbook/CSV). */
  replaceTargets(targetsModel: TargetsModel): TargetsModel {
    if (!targetsModel || !Array.isArray(targetsModel.sets)) {
      throw new Error('Invalid targets model provided.');
    }
    this.customTargets = deepClone(targetsModel);
    return deepClone(this.customTargets);
  }

  hasTargets(): boolean {
    return this.customTargets !== null && this.customTargets.sets.length > 0;
  }

  hasTargetParentData(): boolean {
    const sets = this.customTargets?.sets ?? [];
    return this.customTargets?.provisionParents === true && sets.length > 0 && sets.every((set) =>
      !!set.wellName?.trim() &&
      !!set.wellboreName?.trim()
    );
  }

  getTargetParentPayloads(): TargetParentPayloads {
    if (!this.hasTargetParentData()) {
      throw new Error('The targets import does not contain complete well and wellbore data.');
    }

    const wells = new Map<string, TargetParentPayloads['wells'][number]>();
    const wellbores = new Map<string, TargetParentPayloads['wellbores'][number]>();
    const timestamp = new Date().toISOString();
    for (const set of this.customTargets!.sets) {
      const wellId = set.wellName.trim();
      const wellboreId = set.wellboreName!.trim();
      wells.set(wellId, {
        id: wellId,
        name: wellId,
        fetchable: !!set.location,
        is_off_shore: set.isOffShore ?? false,
        ...(set.location ? { location: deepClone(set.location) } : {}),
        ...(set.groundLevel !== undefined ? { ground_level: set.groundLevel } : {}),
        ...(set.rkb !== undefined ? { rkb: set.rkb } : {}),
        timestamp
      });
      wellbores.set(wellboreId, {
        id: wellboreId,
        name: set.wellboreName!.trim(),
        well_id: wellId,
        timestamp
      });
    }
    return { wells: Array.from(wells.values()), wellbores: Array.from(wellbores.values()) };
  }

  getTargets(): TargetsModel | null {
    return this.customTargets ? deepClone(this.customTargets) : null;
  }

  /**
   * Resolve every imported target set's WELL NAME to the id of a wellbore that
   * is already published in the Consumption Hub.
   *
   * Matching is two-stage:
   *  1. The target set's WELL NAME is matched to a published *well* by name.
   *     The match is tolerant of a leading lease/operator prefix — e.g. the
   *     file's `LHN TOMAHAWK 37-25 A 1011LS` matches the published
   *     `Tomahawk 37-25 A 1011LS`.
   *  2. Once a well is matched, its wellbores (queried via `well_id`) select the
   *     `wellbore_id`:
   *      - exactly one wellbore  → used directly, no name comparison;
   *      - multiple wellbores    → disambiguated by the file's WELLBORE NAME
   *                                (if supplied); otherwise a warning is raised
   *                                and the well is left unresolved;
   *      - no wellbore           → unresolved (nothing to attach targets to).
   *
   * The resolved value is always the published wellbore's ID — the WELL NAME is
   * never assumed to be the id.
   */
  resolveTargetWellbores(
    publishedWells: QuerySummaryDto[],
    wellboresByWellId: Map<string, QuerySummaryDto[]>
  ): TargetWellboreResolution {
    const sets = this.customTargets && Array.isArray(this.customTargets.sets) ? this.customTargets.sets : [];

    if (this.hasTargetParentData()) {
      return {
        resolved: sets.map((set) => ({ wellName: set.wellName.trim(), wellboreId: set.wellboreName!.trim() })),
        unresolved: []
      };
    }

    const resolved: { wellName: string; wellboreId: string }[] = [];
    const unresolved: string[] = [];
    const warnings: string[] = [];
    const seen = new Set<string>();

    for (const set of sets) {
      const wellName = (set.wellName ?? '').trim();
      const key = wellName.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      if (!wellName) {
        unresolved.push('(blank well name)');
        continue;
      }

      const well = this.findMatchingWell(wellName, publishedWells);
      const wellId = (well?.id ?? '').trim();
      if (!wellId) {
        unresolved.push(wellName);
        continue;
      }

      const wellbores = (wellboresByWellId.get(wellId) ?? []).filter((wb) => (wb?.id ?? '').trim());

      if (wellbores.length === 1) {
        // Single wellbore: match without comparing names.
        resolved.push({ wellName, wellboreId: (wellbores[0].id ?? '').trim() });
        continue;
      }

      if (wellbores.length === 0) {
        unresolved.push(wellName);
        warnings.push(
          `Well "${wellName}" was found but has no published wellbore. ` +
          `Publish the wellbore first, then re-import.`
        );
        continue;
      }

      // Multiple wellbores: disambiguate by the file's WELLBORE NAME if present.
      const wellboreName = (set.wellboreName ?? '').trim();
      if (!wellboreName) {
        unresolved.push(wellName);
        warnings.push(
          `Well "${wellName}" has ${wellbores.length} published wellbores but the targets file ` +
          `does not specify a wellbore name. Add a WELLBORE NAME column, then re-import.`
        );
        continue;
      }

      const wellboreMatch = wellbores.find((wb) => this.wellNamesMatch(wellboreName, wb?.name ?? ''));
      const wellboreId = (wellboreMatch?.id ?? '').trim();
      if (wellboreId) {
        resolved.push({ wellName, wellboreId });
      } else {
        unresolved.push(wellName);
        warnings.push(
          `Well "${wellName}" has ${wellbores.length} published wellbores and the supplied wellbore ` +
          `name "${wellboreName}" did not match any of them.`
        );
      }
    }

    return warnings.length ? { resolved, unresolved, warnings } : { resolved, unresolved };
  }

  /**
   * The distinct published *well* ids that the loaded target sets resolve to by
   * name. The caller uses these to fetch each well's wellbores (via `well_id`)
   * before calling {@link resolveTargetWellbores} / {@link getTargetsPayload}.
   */
  matchedWellIds(publishedWells: QuerySummaryDto[]): string[] {
    const sets = this.customTargets && Array.isArray(this.customTargets.sets) ? this.customTargets.sets : [];
    const ids = new Set<string>();
    const seen = new Set<string>();

    for (const set of sets) {
      const wellName = (set.wellName ?? '').trim();
      const key = wellName.toLowerCase();
      if (!wellName || seen.has(key)) continue;
      seen.add(key);

      const well = this.findMatchingWell(wellName, publishedWells);
      const id = (well?.id ?? '').trim();
      if (id) ids.add(id);
    }

    return Array.from(ids);
  }

  /**
   * Build the Targets payload (TargetSetDto[]) from the separately-imported
   * targets model. Sets and points always carry a non-empty id and name
   * (minLength 1), coordinate_input_type defaults to 0 (grid) but honours a
   * per-point override, and every set is stamped with the current timestamp.
   *
  * `wellbore_id` comes directly from a self-contained import when available;
  * otherwise it is resolved against wellbores already published in the
  * Consumption Hub. An unresolved legacy WELL NAME blocks publishing.
   */
  getTargetsPayload(
    publishedWells: QuerySummaryDto[],
    wellboresByWellId: Map<string, QuerySummaryDto[]>
  ): TargetSetDto[] {
    const sets = this.customTargets && Array.isArray(this.customTargets.sets) ? this.customTargets.sets : [];
    if (sets.length === 0) return [];

    const resolution = this.resolveTargetWellbores(publishedWells, wellboresByWellId);
    if (resolution.unresolved.length > 0) {
      throw new Error(
        `Cannot publish targets: ${resolution.unresolved.length} well(s) did not resolve to a ` +
        `published wellbore: ${resolution.unresolved.join(', ')}. ` +
        `Publish the well/wellbore first, or correct the WELL NAME in the targets file.`
      );
    }

    const idByName = new Map(resolution.resolved.map(r => [r.wellName.toLowerCase(), r.wellboreId]));
    const now = new Date().toISOString();

    return sets.map((set) => {
      const wellName = (set.wellName ?? '').trim();
      const wellboreId = idByName.get(wellName.toLowerCase());
      const setId = (set.id ?? '').trim() || `${wellName}-Targets`;
      const setName = (set.name ?? '').trim() || `${wellName} Targets`;

      const targets: TargetDto[] = (Array.isArray(set.targets) ? set.targets : []).map((point, ti) => {
        const pointId = (point.id ?? '').trim() || `${setId}-${ti + 1}`;
        const pointName = (point.name ?? '').trim() || `Target ${ti + 1}`;
        const coordinateInputType =
          typeof point.coordinateInputType === 'number' && Number.isFinite(point.coordinateInputType)
            ? point.coordinateInputType
            : 0;

        const dto: TargetDto = {
          id: pointId,
          name: pointName,
          coordinate_input_type: coordinateInputType
        };
        if (point.northing !== undefined) dto.northing = point.northing;
        if (point.easting !== undefined) dto.easting = point.easting;
        if (point.tvdss !== undefined) dto.tvdss = point.tvdss;
        return dto;
      });

      return {
        id: setId,
        name: setName,
        wellbore_id: wellboreId,
        fetchable: false,
        targets,
        timestamp: now
      };
    });
  }

  /**
   * True when two well/wellbore names are equal, or differ only by a leading
   * (lease/operator) prefix — e.g. `LHN TOMAHAWK 37-25 A 1011LS` matches
   * `Tomahawk 37-25 A 1011LS`. Comparison is case-insensitive with collapsed
   * whitespace, and the extra prefix must break on a word boundary.
   */
  private wellNamesMatch(a: string, b: string): boolean {
    const norm = (s: string) => (s ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
    const na = norm(a);
    const nb = norm(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    if (na.endsWith(' ' + nb)) return true;
    if (nb.endsWith(' ' + na)) return true;
    return false;
  }

  /** Find the published well whose name matches (prefix-tolerant) the target well name. */
  private findMatchingWell(
    targetWellName: string,
    publishedWells: QuerySummaryDto[]
  ): QuerySummaryDto | undefined {
    if (!Array.isArray(publishedWells)) return undefined;
    return publishedWells.find((well) => this.wellNamesMatch(targetWellName, (well?.name ?? '').toString()));
  }

  private clampAzimuth(value: number): number {
    const TWO_PI = Math.PI * 2;
    const normalized = ((value % TWO_PI) + TWO_PI) % TWO_PI;
    // API max is exactly 2π but we keep it strictly < 2π to be safe
    return Math.min(normalized, TWO_PI);
  }

  updateTrajectoryStations(stations: TrajectoryStation[]): void {
    const model = this.getModel();
    model.trajectory.stations = this.validateStations(stations);
    this.cachedModel = model;
  }

  private buildAndValidateModel(modelData: ModelData): ModelData {
    return this.sanitizeModelData(modelData);
  }

  private sanitizeModelData(modelData: ModelData): ModelData {
    if (!modelData || typeof modelData !== 'object') {
      throw new Error('Invalid model data provided.');
    }

    const well = deepClone(modelData.well);
    const wellbore = deepClone(modelData.wellbore);
    const trajectory = deepClone(modelData.trajectory);

    if (!well || !well.id || !well.name) {
      throw new Error('Well data must include id and name.');
    }

    if (!wellbore || !wellbore.id || !wellbore.well_id) {
      throw new Error('Wellbore data must include id and well_id.');
    }

    if (!trajectory || !trajectory.id || !trajectory.wellbore_id) {
      throw new Error('Trajectory data must include id and wellbore_id.');
    }

    if (wellbore.well_id !== well.id) {
      throw new Error('Wellbore well_id must match the well id.');
    }

    if (trajectory.wellbore_id !== wellbore.id) {
      throw new Error('Trajectory wellbore_id must match the wellbore id.');
    }

    well.fetchable = normalizeBooleanFlag(well.fetchable);
    well.location.azimuth_reference = normalizeAzimuthReference(well.location.azimuth_reference);
    wellbore.fetchable = normalizeBooleanFlag(wellbore.fetchable);
    trajectory.azimuth_reference = normalizeAzimuthReference(trajectory.azimuth_reference);

    if (!Array.isArray(trajectory.stations) || trajectory.stations.length === 0) {
      throw new Error('Trajectory stations are required.');
    }

    trajectory.stations = this.validateStations(trajectory.stations);

    return { well, wellbore, trajectory };
  }

  private validateStations(stations: TrajectoryStation[]): TrajectoryStation[] {
    if (!Array.isArray(stations) || stations.length === 0) {
      throw new Error('Trajectory stations are required.');
    }

    const fullRotation = Math.PI * 2;

    return stations.map((station) => {
      const md = Number(station.md);
      const inclination = Number(station.inclination);
      const azimuth = Number(station.azimuth);

      if (!Number.isFinite(md)) throw new Error('Invalid measured depth value.');
      if (!Number.isFinite(inclination)) throw new Error('Invalid inclination value.');
      if (!Number.isFinite(azimuth)) throw new Error('Invalid azimuth value.');

      const sanitized: TrajectoryStation = {
        md,
        inclination,
        azimuth: ((azimuth % fullRotation) + fullRotation) % fullRotation,
        comment: station.comment ?? ''
      };

      if (station.tvd !== undefined) sanitized.tvd = Number(station.tvd);
      if (station.northing !== undefined) sanitized.northing = Number(station.northing);
      if (station.easting !== undefined) sanitized.easting = Number(station.easting);

      return sanitized;
    });
  }
}
